import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, ViewChild, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RoutesRecognized } from '@angular/router';
import { GroupSearchBarComponent } from '@coin/shared/group-search/feature';
import { SearchResultGroup } from '@coin/shared/group-search/util';
import { environment } from '@coin/shared/core/util-environments';
import { GlobalEventsService, UpdateNotificationsService } from '@coin/shared/core/util-helpers';
import { BehaviorSubject, filter, map, Observable } from 'rxjs';
import { EmployeeSearchResultGroup } from '../../search/employee-search-result-group.model';
import { LegalEntitySearchResultGroup } from '../../search/legal-entity-search-result-group.model';
import { OrganisationSearchResultGroup } from '../../search/organisation-search-result-group';
import { SearchService } from '../../search/search.service';
import { UserState } from '@coin/shared/auth/feature';
import { MatIcon } from '@angular/material/icon';
import { UserMenuComponent } from './components/user-menu/user-menu.component';
import { HelpMenuComponent } from './components/help-menu/help-menu.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { FeatureFlagDirective } from '@coin/shared/core/feature-directives';
import { IconButtonComponent, UpdateNotifierComponent } from '@coin/shared/core/ui-storybook';
import { ClickedOutsideDirective } from '@coin/shared/core/util-directives';

@Component({
  selector: 'cosmos-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FeatureFlagDirective,
    MatIcon,
    IconButtonComponent,
    ClickedOutsideDirective,
    GroupSearchBarComponent,
    UpdateNotifierComponent,
    UserMenuComponent,
    HelpMenuComponent,
    AsyncPipe,
    TranslatePipe
  ]
})
export class HeaderComponent {
  private readonly router = inject(Router);
  private readonly userState = inject(UserState);
  private readonly events = inject(GlobalEventsService);
  private readonly searchService = inject(SearchService);
  private readonly destroyRef = inject(DestroyRef);
  protected updateNotifications = inject(UpdateNotificationsService);

  @ViewChild('searchContainer') public searchContainer: ElementRef;
  @ViewChild('searchBar') public searchBar: GroupSearchBarComponent;

  public readonly isSidebarVisible = input<boolean>(undefined);

  public searchResultGroups: SearchResultGroup[] = [
    new OrganisationSearchResultGroup(this.searchService.getOrganisations),
    new EmployeeSearchResultGroup(this.searchService.getEmployees),
    new LegalEntitySearchResultGroup(this.searchService.getLegalEntities)
  ];

  public isSearchBarVisibleOnMobile$ = new BehaviorSubject<boolean>(false);

  public isSearchVisible$: Observable<boolean>;
  protected isLoggedInAsUser$ = this.userState.user$.pipe(map(Boolean));

  public get currentEnvironment(): typeof environment {
    return environment;
  }

  constructor() {
    this.isSearchVisible$ = this.router.events.pipe(
      filter(event => event instanceof RoutesRecognized),
      map(event => {
        const isVisible = (event as RoutesRecognized).state?.root?.firstChild?.data?.isHeaderSearchBarVisible;
        return isVisible == null || isVisible;
      })
    );

    this.events
      .listen('click')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.clickout(event));
  }

  public toggleSearch(): void {
    if (this.isSearchBarVisibleOnMobile$.value) {
      this.isSearchBarVisibleOnMobile$.next(false);
    } else {
      this.isSearchBarVisibleOnMobile$.next(true);

      setTimeout(() => {
        this.searchBar.focusInput();
      }, 0);
    }
  }

  private readonly clickout = (event: MouseEvent): void => {
    const clickedOutside = !this.searchContainer?.nativeElement.contains(event.target);

    if (clickedOutside) {
      this.isSearchBarVisibleOnMobile$.next(false);
    }
  };
}
